import { useMemo, useState } from "react"
import { Grid, MenuItem } from "@mui/material"
import { MRT_ColumnDef } from "material-react-table"
import { useNavigate } from "react-router-dom"
import { enqueueSnackbar } from "notistack"
import { makeStyles } from "tss-react/mui"

import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { useDateTime } from "../../../../localization/useDateTime"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { FetchProductsResponse, MerchantProductsQuery, fetchProducts, removeOffer } from "../../products/productsApi"
import { StandardTableFilter } from "../../../../../shared/components/table/StandardTableFilters"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { DeleteConfirmDialog } from "../../../../../shared/components/dialogs/DeleteConfirmDialog"
import { getEditProductRoute } from "../../../../../constants/routes"
import { tailwindPalette } from "../../../../theme/tailwindPalette"

import { ProductOfferItem } from "../offersTypes"

const useStyles = makeStyles()(() => ({
    removeItem: {
        color: tailwindPalette.red[600],
    },
}))

function transformProductsData(data: FetchProductsResponse): ProductOfferItem[] {
    return data.productGroups.flatMap((group) => {
        const products = group.products.map<ProductOfferItem>((product) => ({
            ...product,
        }))

        return products
    })
}

type RemoveOfferConfirmState = Readonly<{
    open: boolean
    productName: string
    productId: number | null
}>

const defaultRemoveOfferState: RemoveOfferConfirmState = {
    open: false,
    productName: "",
    productId: null,
}

type OffersTabProps = Readonly<{
    priceListId: number
}>

export function OffersTab({ priceListId }: OffersTabProps) {
    const { merchantId } = useMerchant()
    const { l10n } = useAppLocalization()
    const dateTimeFactory = useDateTime()
    const navigate = useNavigate()
    const { classes } = useStyles()
    const [removeOfferState, setRemoveOfferState] = useState<RemoveOfferConfirmState>(defaultRemoveOfferState)

    const products = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: merchantId,
            priceListId,
            deliveryDate: dateTimeFactory.toDate(dateTimeFactory.now()),
        },
        select: transformProductsData,
    })
    const { ApiQueryTable, columnFactory } = useApiQueryTable(products)

    const { mutate: callRemoveOffer } = useApiMutation(removeOffer, {
        async onSuccess(_params, data, context) {
            await context.queryClient.invalidateQueries({ queryKey: [MerchantProductsQuery.LIST] })
            enqueueSnackbar({
                variant: "success",
                message: data.serviceResponse.displayMessage,
            })
        },
    })

    const columns = useMemo<MRT_ColumnDef<ProductOfferItem>[]>(
        () => [
            columnFactory.nameColumn({
                header: l10n.getString("offers-table-name-column"),
                accessorKey: "name",
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("offers-table-price-column"),
                    id: "price.amount",
                    minSize: COLUMN_SIZE.SM,
                },
                (row) => row.price,
                true,
            ),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("offers-table-original-price-column"),
                    id: "offerDetails.discount.originalPrice.amount",
                    size: COLUMN_SIZE.M,
                    minSize: COLUMN_SIZE.M,
                },
                (row) => row?.offerDetails?.discount?.originalPrice,
                true,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("offers-table-valid-from-column"),
                    id: "offerDetails.availabilityDetails.dateTimeInterval.from",
                    minSize: COLUMN_SIZE.M,
                },
                (row) => row?.offerDetails?.availabilityDetails?.dateTimeInterval?.from,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("offers-table-expires-column"),
                    id: "offerDetails.availabilityDetails.dateTimeInterval.to",
                    minSize: COLUMN_SIZE.M,
                },
                (row) => row?.offerDetails?.availabilityDetails?.dateTimeInterval?.to,
            ),
            columnFactory.activeInactiveStatusColumn(
                {
                    header: l10n.getString("offers-table-status-column"),
                    id: "status",
                    minSize: COLUMN_SIZE.SMM,
                },
                (row) => row.isActive,
            ),
        ],
        [columnFactory, l10n],
    )

    return (
        <Grid container direction="column">
            <ApiQueryTable
                columns={columns}
                layoutMode="grid"
                initialState={{ density: "spacious" }}
                renderRowActionMenuItems={({ row, closeMenu }) => [
                    <LocalizedStrict id="offers-table-edit-menu-item" key={`edit-${row.original.id}`}>
                        <MenuItem
                            onClick={() => {
                                navigate(`${getEditProductRoute(row.original.id)}?openOffer=true`)
                                closeMenu()
                            }}
                        >
                            Edit
                        </MenuItem>
                    </LocalizedStrict>,
                    <LocalizedStrict id="offers-table-remove-offer-menu-item" key={`remove-offer-${row.original.id}`}>
                        <MenuItem
                            className={classes.removeItem}
                            onClick={() => {
                                setRemoveOfferState({
                                    open: true,
                                    productName: row.original.name,
                                    productId: row.original.id,
                                })
                                closeMenu()
                            }}
                        >
                            Remove offer
                        </MenuItem>
                    </LocalizedStrict>,
                ]}
                renderToolbarInternalActions={({ table }) => <StandardTableFilter variant="right" table={table} />}
            />
            <LocalizedStrict id="offers-table-remove-offer-confirm-dialog" attrs={{ typeOfEntity: true }}>
                <DeleteConfirmDialog
                    typeOfEntity="offer"
                    entityName={removeOfferState.productName}
                    handleClick={() => {
                        if (removeOfferState.productId != null) {
                            callRemoveOffer({ productId: removeOfferState.productId })
                        }
                    }}
                    onClose={() => setRemoveOfferState(defaultRemoveOfferState)}
                    open={removeOfferState.open}
                />
            </LocalizedStrict>
        </Grid>
    )
}
